import React from "react";
import LiveGradient from "./LiveGradient";

/**
 * GlassBackground — single-component wrapper that gives any page the warm
 * animated mesh-gradient + the standard stacking context for glass-themed UI.
 *
 * The shape mirrors what landingPage.jsx does manually so any page can switch
 * to the theme with one line:
 *
 *   import { GlassBackground } from "../theme";
 *
 *   return (
 *     <GlassBackground>
 *       ...page content...
 *     </GlassBackground>
 *   );
 *
 * Props:
 *   fixed         — when true (default), the gradient stage is viewport-fixed
 *                   so it doesn't scroll with the page. Set false for short
 *                   pages that should let the gradient scroll with content.
 *   className     — extra Tailwind / utility classes appended to the root.
 *   as            — root element tag (default "div"). Use "main" for top-level
 *                   page roots if you want semantic correctness.
 *   gradientClass — override the gradient palette class. Defaults to
 *                   "bg-glass-gradient". Use "bg-glass-gradient-local" for a
 *                   variant that does not promote the wrapper to a containing
 *                   block for fixed children.
 *   children      — page content. Rendered above the gradient automatically
 *                   (the CSS already gives non-gradient children z-index: 1).
 */
export const GlassBackground = React.forwardRef(function GlassBackground(
  {
    fixed = true,
    className = "",
    as: Tag = "div",
    gradientClass = "bg-glass-gradient",
    children,
    ...rest
  },
  ref
) {
  return (
    <Tag
      ref={ref}
      className={`relative ${gradientClass} overflow-hidden ${className}`.trim()}
      {...rest}
    >
      <LiveGradient fixed={fixed} />
      {children}
    </Tag>
  );
});

export default GlassBackground;
